import { z } from 'zod';
import { SAVE_VERSION } from '../state';

/**
 * Zod schemas for the PERSISTED shape (Decimals as strings). Imported save codes
 * are untrusted and must validate before we trust them (TECHNICAL §10). Keep in
 * lockstep with serialize.ts.
 */

const decimalString = z.string().regex(/^-?(\d+(\.\d+)?([eE][+-]?\d+)?|Infinity)$/, 'not a Decimal string');

export const rngSchema = z.object({ seed: z.number(), cursor: z.number() });

export const settingsSchema = z.object({
  notation: z.enum(['standard', 'scientific', 'engineering']),
  reducedMotion: z.boolean(),
});

export const walletSchema = z.object({ scrap: decimalString, components: decimalString, alloy: decimalString });

export const frameSchema = z.object({ defId: z.string(), level: z.number().int().min(1), hp: decimalString });

export const rolledAffixSchema = z.object({
  stat: z.enum(['critChance', 'critMult', 'power', 'cooldown', 'dropRate', 'scrapMult', 'componentMult', 'weightRed']),
  value: decimalString,
});

export const partInstanceSchema = z.object({
  instanceId: z.string(),
  defId: z.string(),
  rarity: z.enum(['common', 'uncommon', 'rare', 'epic', 'legendary', 'unit', 'goated']),
  level: z.number().int().min(1),
  affixes: z.array(rolledAffixSchema),
});

export const inventorySchema = z.object({
  parts: z.array(partInstanceSchema),
  nextId: z.number().int().min(1),
  cap: z.number().int().min(1),
  protectedIds: z.array(z.string()),
});

export const resourcesSchema = z.object({
  cpu: z.number().int().min(0),
  ram: z.number().int().min(0),
  storage: z.number().int().min(1),
});

export const graphNodeSchema = z.object({
  id: z.string(),
  defId: z.string(),
  instanceId: z.string().nullable(),
  level: z.number().int().min(1),
  x: z.number(),
  y: z.number(),
});

export const graphEdgeSchema = z.object({
  id: z.string(),
  fromNode: z.string(),
  fromPort: z.string(),
  toNode: z.string(),
  toPort: z.string(),
});

export const mindSchema = z.object({
  nodes: z.array(graphNodeSchema),
  edges: z.array(graphEdgeSchema),
  nextId: z.number().int().min(1),
});

export const unitVisualSchema = z.object({
  modelId: z.string(),
  paletteId: z.string(),
  loadoutId: z.string(),
  modifierIds: z.array(z.string()),
});

export const enemySchema = z.object({
  id: z.string(),
  visual: unitVisualSchema,
  name: z.string(),
  hp: decimalString,
  maxHp: decimalString,
  dmgPerSec: decimalString,
  scrapReward: decimalString,
  componentReward: decimalString,
  isBoss: z.boolean(),
  size: z.number(),
  resist: z.object({ kinetic: z.number(), energy: z.number(), corrosive: z.number() }),
});

export const combatSchema = z.object({
  wave: z.number().int().min(1),
  highestWave: z.number().int().min(1),
  checkpoint: z.number().int().min(1),
  enemies: z.array(enemySchema),
  nextEnemyId: z.number().int().min(1),
  attackTimers: z.record(z.string(), z.number()),
  killsThisRun: z.number().int().min(0),
  // Full Send / Maximum Effort cooldowns are transient (1s/5s/30s); they reset
  // to ready on load, so they are not serialized. Old saves that still carry
  // overdriveUntil/overdriveCdUntil/manualStrikeCdUntil are harmless: z.object
  // strips unknown keys.
  autoAdvance: z.boolean(),
  currentDps: decimalString,
  pauseKind: z.enum(['defeat', 'victory', 'duelIntro']).nullable(),
  pauseUntilMs: z.number(),
  overlordId: z.string().nullable(),
  enrageMs: z.number().min(0),
});

export const statsSchema = z.object({ totalKills: z.number().int().min(0), bestWave: z.number().int().min(1) });

export const metaSchema = z.object({
  cores: decimalString,
  ascensions: z.number().int().min(0),
  reboots: z.number().int().min(0),
  lifetimeScrap: decimalString,
  coreUpgrades: z.record(z.string(), z.number()),
  unlocks: z.record(z.string(), z.boolean()),
});

export const sortieResultSchema = z.object({
  components: decimalString,
  cache: partInstanceSchema.nullable(),
  broke: z.number().int().min(0),
});

export const droneSortieSchema = z.object({
  tier: z.number().int().min(1),
  dronesSent: z.number().int().min(0),
  endsMs: z.number(),
  status: z.enum(['running', 'returned']),
  result: sortieResultSchema.nullable(),
});

export const dronesSchema = z.object({
  unlocked: z.boolean(),
  bots: z.number().int().min(0),
  broken: z.number().int().min(0),
  clearance: z.number().int().min(1),
  sortie: droneSortieSchema.nullable(),
});

export const overlordsSchema = z.object({
  defeated: z.array(z.string()),
  defeats: z.record(z.string(), z.number()),
});

export const petsSchema = z.object({
  unlocked: z.boolean(),
  collection: z.record(z.string(), z.number()),
  equipped: z.array(z.string()),
});

export const foundrySchema = z.object({
  unlocked: z.boolean(),
  job: z
    .object({
      defId: z.string(),
      rarity: z.enum(['common', 'uncommon', 'rare', 'epic', 'legendary', 'unit', 'goated']),
      endsMs: z.number(),
    })
    .nullable(),
  fused: z.number().int().min(0),
});

export const yardSchema = z.object({
  unlocked: z.boolean(),
  level: z.number().int().min(1),
  trials: z.array(z.object({ petId: z.string(), trialId: z.string(), endsMs: z.number() })),
  trained: z.number().int().min(0),
});

export const smelterSchema = z.object({
  unlocked: z.boolean(),
  queue: z.array(z.object({
    defId: z.string(),
    rarity: z.enum(['common', 'uncommon', 'rare', 'epic', 'legendary', 'unit', 'goated']),
    level: z.number().int().min(1),
  })),
  nextDoneMs: z.number(),
  upgrades: z.record(z.string(), z.number()),
  smelted: z.number().int().min(0),
});

export const railsSchema = z.object({
  unlocked: z.boolean(),
  routes: z.array(z.object({ a: z.number().int().min(1), b: z.number().int().min(1), derailedUntilMs: z.number() })),
  lastDerailMs: z.number(),
  built: z.number().int().min(0),
});

export const takeoverSchema = z.object({
  unlocked: z.boolean(),
  stakes: z.record(z.string(), z.number()),
  nextPayoutMs: z.number(),
  lastShiftMs: z.number(),
  lastPayout: decimalString,
  payouts: z.number().int().min(0),
});

export const gridSchema = z.object({
  unlocked: z.boolean(),
  run: z.object({ targetId: z.string(), endsMs: z.number() }).nullable(),
  compute: decimalString,
  upgrades: z.record(z.string(), z.number()),
  intrusions: z.number().int().min(0),
});

export const districtsSchema = z.object({
  unlocked: z.boolean(),
  baselineWave: z.number().int().min(1),
  assigned: z.object({
    housing: z.number().int().min(0),
    factory: z.number().int().min(0),
    lab: z.number().int().min(0),
    arcade: z.number().int().min(0),
  }),
  popBonus: z.number().int().min(0),
});

export const harvestSchema = z.object({
  unlocked: z.boolean(),
  satellites: z.number().int().min(0),
  launch: z.object({ endsMs: z.number() }).nullable(),
});

export const stateDataSchema = z.object({
  version: z.number(),
  lastTickAt: z.number(),
  elapsedMs: z.number(),
  rng: rngSchema,
  settings: settingsSchema,
  wallet: walletSchema,
  frame: frameSchema,
  inventory: inventorySchema,
  resources: resourcesSchema,
  mind: mindSchema,
  combat: combatSchema,
  stats: statsSchema,
  meta: metaSchema,
  drones: dronesSchema,
  overlords: overlordsSchema,
  pets: petsSchema,
  foundry: foundrySchema,
  yard: yardSchema,
  smelter: smelterSchema,
  rails: railsSchema,
  takeover: takeoverSchema,
  grid: gridSchema,
  districts: districtsSchema,
  harvest: harvestSchema,
});

export type StateData = z.infer<typeof stateDataSchema>;

export const saveBlobSchema = z.object({
  version: z.number().int().min(1),
  savedAt: z.number(),
  data: z.record(z.string(), z.unknown()),
});

export type SaveBlob = z.infer<typeof saveBlobSchema>;

export const CURRENT_SAVE_VERSION = SAVE_VERSION;
